import * as React from 'react';
import { findFontByFamily, getFontUrl, type FontRecord } from '../../utils/fonts';
import { buildTextPathLayoutAtFontSize, loadOpentypeFont } from '../../utils/textMetrics';
import { estimateStrokeWidthFromSvgDocument, estimateStrokeWidthFromImageUrl } from '../../utils/strokeWidth';
import {
  buildDisplaySvg,
  buildTextDisplaySvgFromItem,
  getTrimViewBoxFromItem,
} from '../../utils/svgDocument';
import { sourceUnitsToMm } from '../../utils/editorItemLayout';
import type { EditorItem, ImageItem, TextItem } from '../../Reducers/Configurator/types';

/**
 * Measured stroke widths in *source units* (viewBox units), keyed by item
 * content. Content-derived, so survives item moves/resizes and remounts.
 * null = measurement attempted and failed (don't retry).
 */
const strokeCache = new Map<string, number | null>();
const pendingKeys = new Set<string>();

function itemCacheKey(item: EditorItem): string {
  if (item.type === 'text') {
    if (item.pathData) {
      return `t|${item.id}|${item.viewBoxWidth}|${item.trimViewBoxX ?? 0}|${item.trimViewBoxY ?? 0}|${item.pathData.length}`;
    }
    return `t|${item.fontFamily}|${item.content}`;
  }
  if (item.svgSource) {
    return `s|${item.url}|${item.viewBoxWidth}|${item.trimViewBoxX ?? 0}|${item.trimViewBoxY ?? 0}|${item.svgSource.length}`;
  }
  return `u|${item.url}`;
}

async function measureTextItemFromFont(item: TextItem, fonts: FontRecord[]): Promise<number | null> {
  const fontRecord = findFontByFamily(fonts, item.fontFamily);
  const fontUrl = fontRecord ? getFontUrl(fontRecord) : null;
  if (!fontUrl) return null;

  const font = await loadOpentypeFont(fontUrl);
  if (!font) return null;

  const layout = buildTextPathLayoutAtFontSize(font, item.content);
  const displaySvg = buildTextDisplaySvgFromItem({
    pathData: layout.pathData,
    pathOffsetX: layout.offsetX,
    pathOffsetY: layout.offsetY,
    viewBoxWidth: layout.viewBoxWidth,
    viewBoxHeight: layout.viewBoxHeight,
  });
  if (!displaySvg) return null;
  return estimateStrokeWidthFromSvgDocument(displaySvg);
}

function measureTextItem(item: TextItem, fonts: FontRecord[]): Promise<number | null> {
  const displaySvg = buildTextDisplaySvgFromItem(item);
  if (displaySvg) {
    return estimateStrokeWidthFromSvgDocument(displaySvg);
  }
  return measureTextItemFromFont(item, fonts);
}

function measureImageItem(item: ImageItem): Promise<number | null> {
  if (item.svgSource) {
    const displaySvg = buildDisplaySvg(item.svgSource, getTrimViewBoxFromItem(item));
    return estimateStrokeWidthFromSvgDocument(displaySvg);
  }
  return estimateStrokeWidthFromImageUrl(item.url, item.viewBoxWidth, item.viewBoxHeight);
}

/**
 * Effective stroke width per item id, in millimeters at the item's current size.
 * Items still measuring (or unmeasurable) are absent from the result.
 */
export function useItemStrokeWidths(
  objects: Record<string, EditorItem>,
  fonts: FontRecord[]
): Record<string, number> {
  const [version, setVersion] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;

    Object.values(objects).forEach((item) => {
      const key = itemCacheKey(item);
      if (strokeCache.has(key) || pendingKeys.has(key)) return;
      pendingKeys.add(key);

      const measurement = item.type === 'text' ? measureTextItem(item, fonts) : measureImageItem(item);
      measurement
        .catch(() => null)
        .then((stroke) => {
          pendingKeys.delete(key);
          strokeCache.set(key, stroke);
          if (!cancelled) setVersion((v) => v + 1);
        });
    });

    return () => {
      cancelled = true;
    };
  }, [objects, fonts]);

  return React.useMemo(() => {
    const result: Record<string, number> = {};
    Object.values(objects).forEach((item) => {
      const cached = strokeCache.get(itemCacheKey(item));
      if (typeof cached !== 'number') return;
      const mm = sourceUnitsToMm(item, cached);
      if (mm && mm > 0) {
        result[item.id] = mm;
      }
    });
    return result;
    // strokeCache fills asynchronously; the version bump above re-runs this memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, version]);
}
